import { useEffect } from 'react';
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useGuardianTransactionStatus } from '@/hooks/use-guardian-transaction-status';
import {
  isGuardianTransactionInProgress,
  type GuardianTransactionStatus,
} from '@mls-wallet/shared-types';
import {
  getExecutionStatusMessage,
  PARTIAL_EXECUTION_USER_MESSAGE,
} from '@/components/execution/execution-status-display';

/**
 * Props for ExecutionStatusModal component.
 */
interface ExecutionStatusModalProps {
  /**
   * Controls whether the modal copy refers to proposal or transaction execution.
   */
  executionTarget: 'proposal' | 'transaction';
  /**
   * Whether the modal is open. Controls modal visibility.
   */
  open: boolean;

  /**
   * The guardianTransactionId to poll status for.
   * Pass null to disable polling.
   */
  guardianTransactionId: string | null; // null during initial loading

  /**
   * Optional immediate error message to display.
   * When set, the modal shows an error state immediately without polling.
   * Use this when the execute API call itself fails (before we get a guardianTransactionId).
   */
  immediateError?: string | null;

  /**
   * Callback when execution completes successfully.
   * Called with the createdRecordId if available.
   */
  onSuccess?: (createdRecordId: string | null) => void;

  /**
   * Callback when execution fails or is reverted.
   * Called with the error message.
   */
  onError?: (errorMessage: string | null) => void;

  /**
   * Callback to close the modal.
   */
  onClose: () => void;
}

/**
 * Get the status icon based on the current guardian transaction status.
 */
function getStatusIcon(status: GuardianTransactionStatus | undefined) {
  // Show spinner for in-progress states or when status is not yet known
  if (!status || isGuardianTransactionInProgress(status)) {
    return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
  }

  // Terminal states
  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="h-12 w-12 text-green-500" />;
    case 'PARTIALLY_EXECUTED':
      return <AlertTriangle className="h-12 w-12 text-amber-500" />;
    case 'FAILED':
      return <XCircle className="h-12 w-12 text-red-500" />;
    case 'REVERTED':
      return <AlertTriangle className="h-12 w-12 text-amber-500" />;
    case 'BLOCKED':
      return <XCircle className="h-12 w-12 text-gray-500" />;
    default:
      // Fallback for any unexpected status
      return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
  }
}

/**
 * ExecutionStatusModal - Modal component for displaying async execution status.
 *
 * This modal polls the GuardianTransaction status after an execute endpoint is called.
 * It shows a spinner while execution is in progress and displays success/error
 * states when execution completes.
 *
 * Usage:
 * 1. Call the execute endpoint which returns a guardianTransactionId
 * 2. Open this modal with that ID
 * 3. Modal polls status and shows progress
 * 4. On completion, callbacks are triggered and modal can be closed
 *
 * @example
 * ```tsx
 * const [guardianTxId, setGuardianTxId] = useState<string | null>(null);
 * const [isModalOpen, setIsModalOpen] = useState(false);
 *
 * const handleExecute = async () => {
 *   const { guardianTransactionId } = await executeAction();
 *   setGuardianTxId(guardianTransactionId);
 *   setIsModalOpen(true);
 * };
 *
 * <ExecutionStatusModal
 *   open={isModalOpen}
 *   guardianTransactionId={guardianTxId}
 *   onSuccess={(createdRecordId) => {
 *     // Handle success - e.g., refresh data, show toast
 *     setIsModalOpen(false);
 *     setGuardianTxId(null);
 *   }}
 *   onError={(errorMessage) => {
 *     // Handle error - e.g., show error toast
 *   }}
 *   onClose={() => {
 *     setIsModalOpen(false);
 *     setGuardianTxId(null);
 *   }}
 * />
 * ```
 */
export function ExecutionStatusModal({
  executionTarget,
  open,
  guardianTransactionId,
  immediateError,
  onSuccess,
  onError,
  onClose,
}: ExecutionStatusModalProps) {
  const { data, isFinished, error } = useGuardianTransactionStatus(
    // Only poll if we have a guardianTransactionId and no immediate error
    open && !immediateError ? guardianTransactionId : null
  );

  // Handle immediate errors (when API call itself failed)
  useEffect(() => {
    if (immediateError) {
      onError?.(immediateError);
    }
  }, [immediateError, onError]);

  // Handle terminal states
  useEffect(() => {
    if (!data || !isFinished) return;

    if (data.status === 'COMPLETED') {
      onSuccess?.(data.createdRecordId);
    } else if (data.status === 'PARTIALLY_EXECUTED') {
      onError?.(PARTIAL_EXECUTION_USER_MESSAGE);
    } else if (
      data.status === 'FAILED' ||
      data.status === 'REVERTED' ||
      data.status === 'BLOCKED'
    ) {
      onError?.(data.errorMessage);
    }
  }, [data, isFinished, onSuccess, onError]);

  // Handle fetch errors
  useEffect(() => {
    if (error) {
      onError?.('Failed to fetch execution status');
    }
  }, [error, onError]);

  // Determine display state based on immediate error or polled data
  const hasImmediateError = !!immediateError;
  const status = data?.status;
  const isSuccess = !hasImmediateError && status === 'COMPLETED';
  const requiresSupport = !hasImmediateError && status === 'PARTIALLY_EXECUTED';
  const isError =
    hasImmediateError ||
    status === 'FAILED' ||
    status === 'REVERTED' ||
    status === 'BLOCKED' ||
    requiresSupport;
  // If there's an immediate error or polled error, we're "finished"
  const isEffectivelyFinished = hasImmediateError || isFinished;

  // Determine display message
  const displayErrorMessage = hasImmediateError
    ? immediateError
    : data?.errorMessage;
  const executionNoun =
    executionTarget === 'transaction' ? 'transaction' : 'proposal';

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        // Only allow closing if execution is finished
        if (!isOpen && isEffectivelyFinished) {
          onClose();
        }
      }}
    >
      <DialogContent
        className={
          isEffectivelyFinished
            ? 'sm:max-w-md'
            : 'sm:max-w-md [&>button]:hidden'
        }
        // Prevent closing via escape or clicking outside while in progress
        onEscapeKeyDown={(e) => {
          if (!isEffectivelyFinished) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (!isEffectivelyFinished) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="text-center pt-4">
          <DialogTitle className="text-center mb-4">
            {isSuccess
              ? 'Execution Complete'
              : requiresSupport
                ? 'Contact support'
                : isError
                  ? 'Execution Failed'
                  : `Executing ${executionNoun === 'transaction' ? 'Transaction' : 'Proposal'}`}
          </DialogTitle>
          <DialogDescription className="text-center">
            {isSuccess
              ? `The ${executionNoun} was executed successfully.`
              : requiresSupport
                ? PARTIAL_EXECUTION_USER_MESSAGE
                : isError
                  ? 'There was an error during execution.'
                  : `Please wait while the ${executionNoun} is being executed...`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center py-6 gap-4">
          {/* Show error icon for immediate errors, otherwise use status-based icon */}
          {hasImmediateError ? (
            <XCircle className="h-12 w-12 text-red-500" />
          ) : (
            getStatusIcon(status)
          )}
          {!requiresSupport && (
            <div className="text-sm text-center text-muted-foreground max-w-xs">
              {hasImmediateError
                ? displayErrorMessage
                : getExecutionStatusMessage(
                    status,
                    data?.errorMessage,
                    executionTarget
                  )}
            </div>
          )}

          {/* Show close button when finished */}
          {isEffectivelyFinished && (
            <Button
              onClick={onClose}
              variant={isSuccess ? 'default' : 'outline'}
            >
              {isSuccess ? 'Done' : 'Close'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
